import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppCard, AppField, AppInput, AppButton, AppBadge } from "../../../shared/components/ui";
import { PageHeader } from "../../../shared/components/PageChrome";
import { IconError, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

export function DeviceRegistration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    serial_number: "",
    model: "",
    capacity: "",
    secret_key: "",
    starting_code: "",
    token_count: "1",
    value_divider: "1",
  });
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      await api.post("/devices", {
        serial_number: formData.serial_number.trim(),
        model: formData.model.trim() || undefined,
        capacity: formData.capacity.trim() || undefined,
        secret_key: formData.secret_key.trim(),
        starting_code: parseInt(formData.starting_code, 10),
        token_count: parseInt(formData.token_count, 10) || 1,
        value_divider: Math.max(1, parseInt(formData.value_divider, 10) || 1),
        is_paygo: true,
      });
      navigate(`/devices/${formData.serial_number.trim()}`);
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <div>
      <PageHeader
        title="Register Device"
        subtitle="Single-device registration from manufacturer credentials."
        crumbs={[{ label: "Devices", to: "/devices" }, { label: "Register" }]}
      />

      <AppCard title="Device details">
        {status === "error" && (
          <div className="pg-inline-error" role="alert" style={{ marginBottom: "1rem" }}>
            <IconError size={14} /> {errorMsg}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: "1rem", maxWidth: 520 }}>
            <AppField label="Serial Number">
              <AppInput
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                placeholder="Physical serial on the device"
                required
                disabled={status === "loading"}
              />
            </AppField>

            <AppField label="Model">
              <AppInput
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="e.g. H4G / SunKing Pro"
                disabled={status === "loading"}
              />
            </AppField>

            <AppField label="Capacity">
              <AppInput
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                placeholder="e.g. 20W"
                disabled={status === "loading"}
              />
            </AppField>

            <AppField label="Secret Key (32 hex)">
              <AppInput
                type="password"
                name="secret_key"
                value={formData.secret_key}
                onChange={handleChange}
                placeholder="From manufacturer Key column"
                required
                disabled={status === "loading"}
                autoComplete="off"
              />
            </AppField>

            <AppField label="Starting Code">
              <AppInput
                type="number"
                name="starting_code"
                value={formData.starting_code}
                onChange={handleChange}
                placeholder="OpenPAYGO starting code"
                required
                disabled={status === "loading"}
              />
            </AppField>

            <AppField label="Token Count">
              <AppInput
                type="number"
                name="token_count"
                min={0}
                value={formData.token_count}
                onChange={handleChange}
                required
                disabled={status === "loading"}
              />
            </AppField>

            <AppField label="Value Divider">
              <AppInput
                type="number"
                name="value_divider"
                min={1}
                value={formData.value_divider}
                onChange={handleChange}
                required
                disabled={status === "loading"}
              />
            </AppField>

            <div style={{ display: "flex", gap: 8, marginTop: "0.5rem" }}>
              <AppButton type="submit" disabled={status === "loading"}>
                {status === "loading" ? <><IconSpinner size={14} /> Registering…</> : "Register Device"}
              </AppButton>
              <Link to="/devices">
                <AppButton type="button" variant="secondary" disabled={status === "loading"}>Cancel</AppButton>
              </Link>
              <Link to="/bulk-import">
                <AppButton type="button" variant="secondary" disabled={status === "loading"}>Bulk Import</AppButton>
              </Link>
            </div>
          </div>
        </form>

        {status === "error" && errorMsg && (
          <AppBadge tone="error">{errorMsg}</AppBadge>
        )}
      </AppCard>
    </div>
  );
}
